import React, { useMemo } from "react";
import { Table, Dropdown, OverlayTrigger, Tooltip, Button, Accordion } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import useMediaQuery from "../../hooks/useMediaQuery";

const CustomTable = ({
  data,
  columns,
  orderBy,
  orderType,
  handleOrder,
  actions,
}) => {
  const isTabletOrBelow = useMediaQuery("(max-width: 1341px)");

  const rowsWithIds = useMemo(() => {
    return data?.map(row => ({
      ...row,
      _id: row.id || uuidv4()
    }));
  }, [data]);

  const renderCellContent = (column, rowItem) => {
    const value = column.key?.split('.')?.reduce((obj, key) => obj && obj[key], rowItem);
    
    if (column.formatter) {
      try {
        return column.formatter(value, rowItem);
      } catch (error) {
        console.error(`Error in formatter for column ${column.key}:`, error);
        return 'Errore';
      }
    }
    
    return value ?? 'N/A';
  };

  const renderActions = (rowItem) => {
    const visibleActions = actions?.filter(action => !action.condition || action.condition(rowItem));
    if (visibleActions?.length > 2 && !isTabletOrBelow) {
      return renderDropdownActions(visibleActions, rowItem);
    } else {
      return renderIconActions(visibleActions, rowItem);
    }
  };

  const renderDropdownActions = (actions, rowItem) => {
    const groupedActions = actions.reduce((acc, action) => {
      if (!acc[action.type]) {
        acc[action.type] = [];
      }
      acc[action.type].push(action);
      return acc;
    }, {});

    return (
      <Dropdown drop="start">
        <Dropdown.Toggle variant="light" size="sm" className="no-arrow">
          <i className="bi bi-three-dots-vertical"></i>
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {Object.entries(groupedActions)?.map(([type, actions], groupIndex) => (
            <React.Fragment key={type}>
              {groupIndex > 0 && <Dropdown.Divider />}
              <Dropdown.Header>{type}</Dropdown.Header>
              {actions?.map((action, index) => (
                <Dropdown.Item
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick(rowItem);
                  }}
                >
                  <i className={`bi ${typeof action.icon === "function" ? action.icon(rowItem) : action.icon} me-2`}></i>
                  {action.label}
                </Dropdown.Item>
              ))}
            </React.Fragment>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const renderIconActions = (actions, rowItem) => {
    return (
      <div className="d-flex justify-content-center align-items-center">
        {actions?.map((action, index) => (
          <OverlayTrigger
            key={index}
            placement="top"
            overlay={<Tooltip id={`tooltip-${index}`}>{action.label}</Tooltip>}
          >
            <Button
              variant="light"
              size="sm"
              className="mx-1 p-1"
              onClick={(e) => {
                e.stopPropagation();
                action.onClick(rowItem);
              }}
            >
              <i className={`bi ${typeof action.icon === "function" ? action.icon(rowItem) : action.icon}`}></i>
            </Button>
          </OverlayTrigger>
        ))}
      </div>
    );
  };

  const renderMobileView = () => (
    <Accordion>
      {rowsWithIds?.map((rowItem) => (
        <Accordion.Item 
          eventKey={rowItem._id} 
          key={rowItem._id}
        >
          <Accordion.Header>
            {renderCellContent(columns[1], rowItem)}{" "}
            {renderCellContent(columns[2], rowItem)}
          </Accordion.Header>
          <Accordion.Body>
            {columns?.map((column) => (
              <div key={`${rowItem._id}-${column.key}`} className="mb-2">
                <strong>{column.label}:</strong>{" "}
                {renderCellContent(column, rowItem)}
              </div>
            ))}
            <div className="mt-3" style={{whiteSpace: 'break-spaces'}}>
              {renderActions(rowItem)}
            </div>
          </Accordion.Body>
        </Accordion.Item>
      ))}
    </Accordion>
  );

  const renderDesktopView = () => (
    <Table striped bordered hover className="mt-4">
      <thead className="bg-dark text-light">
        <tr>
          {columns?.map((column) => (
            <th key={column.key || uuidv4()} scope="col" style={{ width: column.width || 'auto', whiteSpace: 'nowrap' }}>
              <div className="d-flex justify-content-between">
                <span className="align-self-center">
                  {column.label}
                  {orderBy === column.key && orderType === "asc" && (
                    <i className={`bi bi-arrow-up ms-1`}></i>
                  )}
                  {orderBy === column.key && orderType === "desc" && (
                    <i className={`bi bi-arrow-down ms-1`}></i>
                  )}
                </span>
                {!column.hideOrder && (
                  <Dropdown>
                    <Dropdown.Toggle size="sm" variant="secondary">
                      <i className="bi bi-list"></i>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item
                        onClick={() => handleOrder(column.key, "asc")}
                      >
                        <i className="bi bi-arrow-up me-1"></i> Ascendente
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => handleOrder(column.key, "desc")}
                      >
                        <i className="bi bi-arrow-down me-1"></i> Discendente
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                )}
              </div>
            </th>
          ))}
          {actions?.length > 0 && (
            <th scope="col" style={{ width: '1%', whiteSpace: 'nowrap' }}>
              <div className="text-center">Azioni</div>
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {rowsWithIds?.map((rowItem) => (
          <tr key={rowItem._id}>
            {columns?.map((column) => (
              <td key={`${rowItem._id}-${column.key}`} className="word-wrap-custom-table" style={{ width: column.width || 'auto', whiteSpace: 'normal' }}>
                {renderCellContent(column, rowItem)}
              </td>
            ))}
            {actions?.length > 0 && (
              <td className="text-center" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                {renderActions(rowItem)}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </Table>
  );

  return (
    <div className={`py-3`}>
      {isTabletOrBelow ? renderMobileView() : renderDesktopView()}
    </div>
  );
};

export default CustomTable;
